import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Card, Icon } from '../../ui/index.js';
import { cn } from '../../lib/cn.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { rememberAlbumKey } from '../albums/albumKeys.js';

type Filter = 'all' | 'photos' | 'albums';

interface DecryptedAlbum {
  album: dto.Album;
  name: string;
  // Path of ancestor names (root → leaf, excluding the leaf), used for
  // display so two "Mare" sub-albums under different roots are
  // distinguishable in the result list.
  breadcrumb: string[];
}

interface DecryptedPhoto {
  photo: dto.Photo;
  filename: string;
  // Album leaf where the photo lives, decoded for the result row.
  albumName: string;
}

interface RootContext {
  rootId: string;
  rootName: string;
  albumKey: CryptoKey;
  // (id → name) map for every node in the root, used to label photo results.
  nameById: Map<string, string>;
}

const MIN_QUERY_FOR_PHOTOS = 2;

export function SearchPage() {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [albums, setAlbums] = useState<DecryptedAlbum[] | 'loading' | { error: string }>('loading');
  const [rootContexts, setRootContexts] = useState<RootContext[]>([]);
  // Photo search is per-root and on-demand: track which root the user is
  // currently scoping photos against, plus the loaded set + its loading state.
  const [photoRootId, setPhotoRootId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<DecryptedPhoto[] | 'idle' | 'loading' | { error: string }>(
    'idle',
  );

  // Initial load: fetch all my albums, build root contexts (unwrap K_album
  // for each root, decrypt every node's name). Cheap enough to do up-front.
  useEffect(() => {
    let cancelled = false;
    setAlbums('loading');
    (async () => {
      try {
        const { albums: list } = await metadata.listAlbums(session.jwt);
        const roots = list.filter((a) => a.parentId === null && a.wrappedAlbumKey);

        const contexts: RootContext[] = [];
        const decryptedNodes: DecryptedAlbum[] = [];

        for (const root of roots) {
          if (!root.wrappedAlbumKey) continue;
          const albumKey = await cv.unwrapAlbumKey(root.wrappedAlbumKey, session.rsaPriv);
          rememberAlbumKey(root.rootAlbumId, albumKey);

          const { nodes } = await metadata.fetchAlbumTree(root.rootAlbumId, session.jwt);
          const nameById = new Map<string, string>();
          const namedNodes: Array<{ album: dto.Album; name: string }> = [];
          for (const node of nodes) {
            try {
              const name = await cv.decryptText(node.encryptedName, albumKey);
              nameById.set(node.id, name);
              namedNodes.push({ album: node, name });
            } catch {
              nameById.set(node.id, '(decrittura fallita)');
              namedNodes.push({ album: node, name: '(decrittura fallita)' });
            }
          }
          const rootName = nameById.get(root.rootAlbumId) ?? '(senza nome)';
          contexts.push({ rootId: root.rootAlbumId, rootName, albumKey, nameById });

          // Build breadcrumbs for every node in this root.
          for (const { album, name } of namedNodes) {
            const breadcrumb: string[] = [];
            let cursor: string | null = album.parentId;
            const seen = new Set<string>();
            while (cursor && !seen.has(cursor)) {
              seen.add(cursor);
              const parentName = nameById.get(cursor);
              if (!parentName) break;
              breadcrumb.unshift(parentName);
              const parentNode = nodes.find((n) => n.id === cursor);
              cursor = parentNode?.parentId ?? null;
            }
            decryptedNodes.push({ album, name, breadcrumb });
          }
        }

        if (cancelled) return;
        setRootContexts(contexts);
        decryptedNodes.sort((a, b) => a.name.localeCompare(b.name));
        setAlbums(decryptedNodes);
      } catch (err) {
        if (cancelled) return;
        setAlbums({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.jwt, session.rsaPriv, metadata]);

  const matchedAlbums = useMemo(() => {
    if (albums === 'loading' || 'error' in albums) return [];
    const q = query.trim().toLowerCase();
    if (q.length === 0) return albums.slice(0, 24);
    return albums.filter(
      (a) =>
        a.name.toLowerCase().includes(q) || a.breadcrumb.some((b) => b.toLowerCase().includes(q)),
    );
  }, [albums, query]);

  // Photo search: when the user focuses the photos tab and selects a root,
  // load every photo of every album under that root and decrypt the meta.
  // The decrypted set is then filtered locally by filename.
  useEffect(() => {
    if (filter === 'albums') return; // photos pane not visible
    if (!photoRootId) return;
    let cancelled = false;
    const ctx = rootContexts.find((c) => c.rootId === photoRootId);
    if (!ctx) return;

    setPhotos('loading');
    (async () => {
      try {
        const albumIds = [...ctx.nameById.keys()];
        const lists = await Promise.all(
          albumIds.map((id) => metadata.listAlbumPhotos(id, session.jwt).then((r) => r.photos)),
        );
        const flat = lists.flat();
        const decrypted: DecryptedPhoto[] = await Promise.all(
          flat.map(async (p) => {
            try {
              const json = await cv.decryptText(p.encryptedMeta, ctx.albumKey);
              const meta = JSON.parse(json) as { filename?: string };
              return {
                photo: p,
                filename: meta.filename ?? p.id,
                albumName: ctx.nameById.get(p.albumId) ?? '(album)',
              };
            } catch {
              return {
                photo: p,
                filename: '(meta non leggibile)',
                albumName: ctx.nameById.get(p.albumId) ?? '(album)',
              };
            }
          }),
        );
        if (cancelled) return;
        setPhotos(decrypted);
      } catch (err) {
        if (cancelled) return;
        setPhotos({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter, photoRootId, rootContexts, session.jwt, metadata]);

  const matchedPhotos = useMemo(() => {
    if (photos === 'idle' || photos === 'loading' || 'error' in photos) return [];
    const q = query.trim().toLowerCase();
    if (q.length < MIN_QUERY_FOR_PHOTOS) return [];
    return photos.filter((p) => p.filename.toLowerCase().includes(q)).slice(0, 80);
  }, [photos, query]);

  const showAlbums = filter === 'all' || filter === 'albums';
  const showPhotos = filter === 'all' || filter === 'photos';

  return (
    <div className="flex flex-col gap-6">
      <SearchHeader query={query} setQuery={setQuery} />
      <FilterChips filter={filter} setFilter={setFilter} />

      {showPhotos ? (
        <PhotoScopePicker
          rootContexts={rootContexts}
          photoRootId={photoRootId}
          setPhotoRootId={(id) => {
            setPhotoRootId(id);
            setPhotos('idle');
          }}
        />
      ) : null}

      {showAlbums ? (
        <ResultSection
          title="Album"
          icon="folder"
          empty={
            albums === 'loading'
              ? 'Carico…'
              : 'error' in albums
                ? `Errore: ${albums.error}`
                : query.trim().length === 0
                  ? 'Inizia a digitare per filtrare i tuoi album.'
                  : matchedAlbums.length === 0
                    ? 'Nessun album corrisponde.'
                    : null
          }
        >
          {matchedAlbums.length > 0 ? (
            <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {matchedAlbums.map((a) => (
                <li key={a.album.id}>
                  <AlbumResult album={a} />
                </li>
              ))}
            </ul>
          ) : null}
        </ResultSection>
      ) : null}

      {showPhotos ? (
        <ResultSection
          title="Foto"
          icon="photo"
          empty={
            !photoRootId
              ? 'Scegli un albero radice per cercare tra le foto.'
              : photos === 'loading'
                ? 'Decifro le foto…'
                : photos === 'idle'
                  ? null
                  : 'error' in photos
                    ? `Errore: ${photos.error}`
                    : query.trim().length < MIN_QUERY_FOR_PHOTOS
                      ? `Digita almeno ${MIN_QUERY_FOR_PHOTOS} caratteri.`
                      : matchedPhotos.length === 0
                        ? 'Nessuna foto corrisponde.'
                        : null
          }
        >
          {matchedPhotos.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {matchedPhotos.map((p) => (
                <li key={p.photo.id}>
                  <PhotoResult photo={p} />
                </li>
              ))}
            </ul>
          ) : null}
        </ResultSection>
      ) : null}
    </div>
  );
}

function SearchHeader({ query, setQuery }: { query: string; setQuery: (q: string) => void }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-outline bg-surface-container-lowest px-5 py-4 transition-all duration-150',
        'focus-within:border-cyan focus-within:ring-4 focus-within:ring-cyan/15',
      )}
    >
      <Icon name="search" className="text-2xl text-on-surface-variant" />
      <input
        autoFocus
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Cerca foto e album"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="min-w-0 flex-1 bg-transparent text-base text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none"
      />
      {query ? (
        <button
          type="button"
          onClick={() => setQuery('')}
          aria-label="Pulisci"
          className="rounded-full p-1 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface"
        >
          <Icon name="close" />
        </button>
      ) : null}
    </div>
  );
}

function FilterChips({ filter, setFilter }: { filter: Filter; setFilter: (f: Filter) => void }) {
  const items: Array<{ key: Filter; label: string; icon: string }> = [
    { key: 'all', label: 'Tutti', icon: 'apps' },
    { key: 'photos', label: 'Foto', icon: 'photo' },
    { key: 'albums', label: 'Album', icon: 'folder' },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const active = filter === it.key;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => setFilter(it.key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors',
              active
                ? 'bg-cyan text-navy'
                : 'border border-outline bg-surface-container-lowest text-on-surface-variant hover:border-on-surface-variant/40 hover:text-on-surface',
            )}
          >
            <Icon name={it.icon} className="text-base" />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

function PhotoScopePicker({
  rootContexts,
  photoRootId,
  setPhotoRootId,
}: {
  rootContexts: RootContext[];
  photoRootId: string | null;
  setPhotoRootId: (id: string | null) => void;
}) {
  if (rootContexts.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-xs uppercase tracking-[0.08em] text-on-surface-variant">
        Cerca foto in
      </span>
      {rootContexts.map((c) => {
        const active = c.rootId === photoRootId;
        return (
          <button
            key={c.rootId}
            type="button"
            onClick={() => setPhotoRootId(active ? null : c.rootId)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              active
                ? 'bg-cyan/15 text-cyan ring-1 ring-cyan/40'
                : 'border border-outline bg-surface-container-lowest text-on-surface-variant hover:border-on-surface-variant/40',
            )}
          >
            <Icon name="folder_special" className="text-base" />
            {c.rootName}
          </button>
        );
      })}
    </div>
  );
}

function ResultSection({
  title,
  icon,
  children,
  empty,
}: {
  title: string;
  icon: string;
  children?: React.ReactNode;
  empty: string | null;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
        <Icon name={icon} className="text-base" />
        {title}
      </div>
      {empty ? (
        <Card className="flex items-center gap-3 px-5 py-4 text-sm text-on-surface-variant">
          <Icon name="info" className="text-lg text-on-surface-variant/70" />
          <span>{empty}</span>
        </Card>
      ) : (
        children
      )}
    </section>
  );
}

function AlbumResult({ album }: { album: DecryptedAlbum }) {
  return (
    <Link
      to={`/albums/${album.album.id}`}
      className="group flex items-center gap-3 rounded-xl border border-outline bg-surface-container-lowest px-4 py-3 transition-colors hover:border-on-surface-variant/40 hover:bg-surface-container-low"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
        <Icon name="folder" filled />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-display text-sm font-semibold text-on-surface">
          {album.name}
        </span>
        {album.breadcrumb.length > 0 ? (
          <span className="truncate text-xs text-on-surface-variant">
            {album.breadcrumb.join(' › ')}
          </span>
        ) : null}
      </span>
      <Icon
        name="arrow_forward"
        className="text-base text-on-surface-variant transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );
}

function PhotoResult({ photo }: { photo: DecryptedPhoto }) {
  return (
    <Link
      to={`/albums/${photo.photo.albumId}`}
      className="group flex items-center gap-3 rounded-xl border border-outline bg-surface-container-lowest px-4 py-3 transition-colors hover:border-on-surface-variant/40 hover:bg-surface-container-low"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
        <Icon name="photo" filled />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm text-on-surface">{photo.filename}</span>
        <span className="truncate text-xs text-on-surface-variant">in {photo.albumName}</span>
      </span>
      <Icon
        name="arrow_forward"
        className="text-base text-on-surface-variant transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );
}
